import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, QrCode, Upload, Monitor } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import QRCodeImg from '../../../img/QRCode.png';
import uploadImg from '../../../img/upload.png';
import screenImg from '../../../img/screen.png';

interface CardData {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  color: string;
  image: string;
}

const cardData: CardData[] = [
  {
    id: 1,
    title: "QR読み込み",
    subtitle: "かんたんアクセス",
    description: "QRコードを読み込むだけで\nすぐに写真投稿画面にアクセスできます。",
    color: "#c7aa7a",
    image: QRCodeImg
  },
  {
    id: 2,
    title: "画像アップロード",
    subtitle: "思い出を共有",
    description: "スマートフォンから大切な思い出の写真を\nアップロードできます。",
    color: "#b29861",
    image: uploadImg
  },
  {
    id: 3,
    title: "スクリーン表示",
    subtitle: "みんなで鑑賞",
    description: "アップロードされた写真が大画面に表示され\n皆で思い出を共有できます。",
    color: "#a08751",
    image: screenImg
  }
];

interface SlideCardsProps {
  className?: string;
}

const SlideCards: React.FC<SlideCardsProps> = ({ className = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? cardData.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      (prevIndex + 1) % cardData.length
    );
  };

  // タッチ・スワイプ対応
  const swipeHandlers = useSwipeable({
    onSwipedLeft: goToNext,
    onSwipedRight: goToPrevious,
    trackMouse: true,
    preventScrollOnSwipe: true,
  });

  return (
    <section className={`slide-cards-section ${className}`}>
      <div className="section-header">
        <div className="title-decoration">
          <div className="decoration-line"></div>
          <span className="section-heart">♥</span>
          <div className="decoration-line"></div>
        </div>
        <h2 className="section-title">3つのカンタンステップ</h2>
        <p className="section-subtitle">
          誰でも簡単に、思い出の写真を共有できます
        </p>
      </div>

  <div className="slide-cards-container" {...swipeHandlers}>
    {/* 左矢印 */}
    <button
      className={`slide-arrow slide-arrow-left ${currentIndex === 0 ? 'disabled' : ''}`}
      onClick={goToPrevious}
      aria-label="前のステップ"
      disabled={currentIndex === 0}
    >
      <ChevronLeft size={24} />
    </button>

    {/* カードスライダー */}
    <div className="cards-wrapper">
      <div 
        className="cards-track"
        style={{ 
          transform: `translateX(-${currentIndex * (100 / cardData.length)}%)`,
          width: `${cardData.length * 100}%`
        }}
      >
        {cardData.map((card) => (
          <div key={card.id} className="slide-card">
            <div className="card-number">{card.id}</div>
            <div className="card-visual-section">
              <div className="card-image-container">
                <img 
                  src={card.image} 
                  alt={card.title}
                  className="card-image"
                />
              </div>
            </div>
            <div className="card-content">
              <h3 className="card-title">{card.title}</h3>
              <p className="card-subtitle">{card.subtitle}</p>
              <p className="card-description">{card.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* 右矢印 */}
    <button
      className={`slide-arrow slide-arrow-right ${currentIndex === cardData.length - 1 ? 'disabled' : ''}`}
      onClick={goToNext}
      aria-label="次のステップ"
      disabled={currentIndex === cardData.length - 1}
    >
      <ChevronRight size={24} />
    </button>
  </div>

      {/* インジケーター（ドット） */}
      <div className="slide-indicators">
        {cardData.map((_, index) => (
          <button
            key={index}
            className={`indicator ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`ステップ ${index + 1} に移動`}
          />
        ))}
      </div>
    </section>
  );
};

export default SlideCards;